import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Trophy, Sparkles, Clock } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { store } from '../lib/localStore';
import { api } from '../lib/api';
import { WinnerReveal } from '../components/WinnerReveal';

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function Challenge() {
  const { t, lang } = useLang();
  const { config } = useEvent();
  const eventId = config?.event?.id;
  const settings = config?.settings || {};

  const [phase, setPhase] = useState('intro'); // intro | form | quiz | result | already
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [revealDismissed, setRevealDismissed] = useState(false);
  const startTime = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!eventId) return;
    const token = store.getToken(eventId);
    try {
      const mine = await api.quizMine(eventId, token);
      if (mine.attempted) { setResult({ score: mine.score, total: mine.total }); setPhase('already'); }
    } catch { /* ignore */ }
    try { setLeaderboard(await api.quizLeaderboard(eventId)); } catch { /* ignore */ }
    const c = store.getContact();
    setForm((f) => ({ ...f, first_name: c.first_name || '', last_name: c.last_name || '', email: c.email || '', phone: c.phone || '' }));
  }, [eventId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // poll for a live winner reveal during the event
  useEffect(() => {
    if (!eventId) return;
    const id = setInterval(() => { api.quizLeaderboard(eventId).then(setLeaderboard).catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, [eventId]);

  const winner = leaderboard?.winner;
  const revealKey = winner ? `0gb:revealseen:${eventId}` : null;
  const showReveal = !!winner && !revealDismissed && sessionStorage.getItem(revealKey) !== (winner.name + winner.score);
  const dismissReveal = () => {
    if (winner) sessionStorage.setItem(revealKey, winner.name + winner.score);
    setRevealDismissed(true);
  };

  const quizOpen = settings.quiz_enabled && settings.quiz_open;

  const beginQuiz = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(t('requiredFields')); return;
    }
    setBusy(true);
    const token = store.getToken(eventId);
    try {
      const res = await api.quizStart({ event_id: eventId, token, lang });
      if (!res.questions || res.questions.length === 0) {
        toast.error(t('quizClosed')); setBusy(false); return;
      }
      setQuestions(res.questions);
      setAttemptId(res.attempt_id);
      setCurrent(0); setAnswers({});
      startTime.current = Date.now();
      setPhase('quiz');
    } catch (e) {
      if (e?.response?.status === 409) { setPhase('already'); toast.error(t('alreadyPlayed')); }
      else toast.error(t('errorGeneric'));
    } finally { setBusy(false); }
  };

  const pick = (qId, idx) => {
    setAnswers((a) => ({ ...a, [qId]: idx }));
    setTimeout(() => {
      if (current < questions.length - 1) setCurrent((c) => c + 1);
    }, 220);
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    const token = store.getToken(eventId);
    const duration = Math.round((Date.now() - startTime.current) / 1000);
    const payload = {
      event_id: eventId, token, attempt_id: attemptId,
      first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
      duration_seconds: duration,
      answers: questions.map((q) => ({ question_id: q.id, selected: answers[q.id] ?? -1 })),
    };
    try {
      const res = await api.quizSubmit(payload);
      store.setContact({ first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone });
      setResult(res);
      setPhase('result');
      try { setLeaderboard(await api.quizLeaderboard(eventId)); } catch { /* ignore */ }
    } catch (e) {
      if (e?.response?.status === 409) { setPhase('already'); }
      else toast.error(t('errorGeneric'));
    } finally { setBusy(false); }
  };

  const answeredCount = Object.keys(answers).length;

  const revealOverlay = showReveal
    ? <WinnerReveal winner={winner} prizeText={leaderboard?.winner_prize_text} onDismiss={dismissReveal} />
    : null;

  // ---------- winner banner (shared) ----------
  const WinnerBanner = () => (leaderboard?.winner ? (
    <div className="mt-6 bg-gradient-to-b from-[#D4AF37]/20 to-[#111A38] border border-[#D4AF37]/50 rounded-2xl p-5 text-center" data-testid="quiz-winner">
      <div className="text-4xl">🏆</div>
      <h3 className="font-mono text-[10px] tracking-widest uppercase text-[#E5C158] mt-2">{t('challengeWinner')}</h3>
      <p className="font-serif text-2xl text-[#FDFBF7] mt-1">{leaderboard.winner.name}</p>
      <p className="font-mono text-sm text-[#E5C158] mt-1">{leaderboard.winner.score}/{leaderboard.winner.total} · {fmtTime(leaderboard.winner.duration_seconds)}</p>
      <p className="text-xs text-slate-300 mt-2">{leaderboard.winner_prize_text}</p>
    </div>
  ) : null);

  const Leaderboard = () => (leaderboard?.visible && leaderboard.leaderboard.length > 0 ? (
    <div className="mt-6" data-testid="quiz-leaderboard">
      <h3 className="font-serif text-xl text-[#E5C158] mb-3">{t('leaderboard')}</h3>
      <div className="space-y-1.5">
        {leaderboard.leaderboard.map((r) => (
          <div key={r.rank} className="flex items-center justify-between bg-[#111A38] border border-[#D4AF37]/15 rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-[#D4AF37] w-5">{r.rank}</span>
              <span className="font-sans text-sm text-slate-200">{r.name}</span>
            </div>
            <span className="font-mono text-xs text-slate-400">{r.score}/{r.total} · {fmtTime(r.duration_seconds)}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null);

  // ---------- phases ----------
  if (phase === 'quiz') {
    const q = questions[current];
    return (
      <div data-testid="quiz-question-screen">
        {revealOverlay}
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[11px] tracking-widest uppercase text-[#E5C158]">{t('question')} {current + 1} {t('of')} {questions.length}</span>
        </div>
        <div className="w-full bg-[#111A38] h-2 rounded-full overflow-hidden border border-[#D4AF37]/20 mb-6">
          <div className="bg-gradient-to-r from-[#D4AF37] to-[#E5C158] h-full transition-all duration-300" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
        </div>

        <h2 className="font-serif text-2xl text-[#FDFBF7] leading-snug mb-5" data-testid="quiz-question-text">{q.question}</h2>

        <div className="space-y-3">
          {q.options.map((opt, idx) => {
            const selected = answers[q.id] === idx;
            return (
              <button
                key={idx}
                data-testid={`quiz-answer-option-${idx}`}
                onClick={() => pick(q.id, idx)}
                className={`w-full min-h-[56px] p-4 text-left rounded-xl text-base font-medium transition-all border flex items-center ${
                  selected ? 'bg-[#D4AF37]/20 border-[#E5C158] text-[#FDFBF7]' : 'bg-[#111A38] border-[#D4AF37]/25 text-slate-100'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {current === questions.length - 1 && (
          <button
            data-testid="quiz-submit-button"
            onClick={submit}
            disabled={busy || answeredCount < questions.length}
            className="mt-6 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl active:scale-[0.98] transition-all disabled:opacity-40"
          >
            {busy ? t('loading') : t('submitQuiz')}
          </button>
        )}
      </div>
    );
  }

  if (phase === 'result' || phase === 'already') {
    return (
      <div data-testid="quiz-result-screen">
        {revealOverlay}
        <div className="text-center py-6 bg-gradient-to-b from-[#111A38] to-[#0B132B] border border-[#D4AF37]/30 rounded-2xl">
          <Sparkles size={36} className="text-[#D4AF37] mx-auto" />
          <p className="font-mono text-[10px] tracking-widest uppercase text-slate-400 mt-3">{t('yourScore')}</p>
          <p className="font-serif font-bold text-5xl text-[#E5C158] mt-1">{result?.score} / {result?.total}</p>
          <p className="font-serif text-lg text-[#FDFBF7] mt-2">{t('excellent')} 🥂</p>
          <p className="text-sm text-slate-400 mt-2">{phase === 'already' ? t('alreadyPlayed') : t('scoreRecorded')}</p>
        </div>
        <WinnerBanner />
        <Leaderboard />
      </div>
    );
  }

  if (phase === 'form') {
    return (
      <div data-testid="quiz-form-screen">
        {revealOverlay}
        <h1 className="font-serif font-bold text-3xl text-[#FDFBF7]">{t('challenge_title')}</h1>
        <p className="text-sm text-slate-400 mt-1 mb-5">{t('quizInfoNote')}</p>
        <div className="space-y-3">
          <input data-testid="quiz-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={t('firstName')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          <input data-testid="quiz-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={t('lastName')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          <input data-testid="quiz-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('email')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
          <input data-testid="quiz-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('phone')} className="w-full bg-[#070C1E] border border-[#D4AF37]/25 rounded-lg p-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] outline-none" />
        </div>
        <button data-testid="quiz-begin-button" onClick={beginQuiz} disabled={busy} className="mt-5 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl active:scale-[0.98] transition-all disabled:opacity-60">
          {busy ? t('loading') : t('beginQuiz')}
        </button>
      </div>
    );
  }

  // intro
  return (
    <div data-testid="quiz-intro-screen">
      {revealOverlay}
      <div className="text-center py-4">
        <Trophy size={44} className="text-[#D4AF37] mx-auto" />
        <h1 className="font-serif font-bold text-3xl text-[#FDFBF7] mt-3">{t('challenge_title')}</h1>
        <p className="font-serif text-xl text-[#E5C158] mt-3">{settings.quiz_questions_per_attempt || 15} {t('challenge_intro')}</p>
        <p className="text-sm text-slate-300 mt-3 px-2">{settings.prize_text}</p>
      </div>

      {quizOpen ? (
        <button data-testid="quiz-start-button" onClick={() => setPhase('form')} className="mt-4 w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-bold py-4 rounded-xl active:scale-[0.98] transition-all">
          {t('startChallenge')}
        </button>
      ) : (
        <div data-testid="quiz-closed" className="mt-4 flex items-center justify-center gap-2 border border-[#D4AF37]/25 text-slate-400 py-4 rounded-xl">
          <Clock size={16} /> {t('quizClosed')}
        </div>
      )}

      <WinnerBanner />
      <Leaderboard />
    </div>
  );
}
